import { Footer } from "@/components/footer"
import { MessageCircle, Mail, Phone, FileQuestion, Clock, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SEO } from "@/components/SEO"

export default function SupportPage() {
  const faqs = [
    {
      question: "How does the negotiation process work?",
      answer:
        "Post your booking request with your preferred price range. Service providers will send you offers, and you can accept the best one or negotiate further.",
    },
    {
      question: "Are service providers verified?",
      answer:
        "Yes, all service providers go through our verification process, including business registration checks and customer reviews.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept major credit cards, debit cards, mobile money, and digital wallets. All payments are processed securely.",
    },
    {
      question: "Can I cancel my booking?",
      answer:
        "Cancellation policies vary by provider. Check the specific terms before confirming your booking. Most providers offer flexible cancellation options.",
    },
    {
      question: "How do I become a service provider?",
      answer:
        "Visit our 'For Providers' page and complete the registration form. Our team will guide you through the verification process.",
    },
    {
      question: "Is there a booking fee?",
      answer:
        "StayApp charges a small service fee on confirmed bookings. The fee is clearly shown before you confirm your booking.",
    },
  ]

  return (
    <>
      <SEO
        title="Support & Help Center - StayApp | FAQs, Contact & Assistance"
        description="Get help with StayApp bookings, payments, and account issues. Browse FAQs, contact support via chat, email, or phone. Available 24/7 for customer assistance."
        keywords="StayApp support, help center, customer service, FAQs, booking help, payment support"
        ogImage="/logo.png"
        ogUrl="https://stayapp.com/support"
        canonical="https://stayapp.com/support"
      />
      <main className="pt-28 md:pt-36 lg:pt-40">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-[#75055B] px-2">
            How Can We Help?
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Get support for your bookings, account, or any questions about StayApp
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 px-2">
              Contact Support
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: MessageCircle,
                title: "Live Chat",
                description: "Chat with our support team",
                action: "Start Chat",
                available: "Available 24/7",
              },
              {
                icon: Mail,
                title: "Email Support",
                description: "support@stayapp.com",
                action: "Send Email",
                available: "Response within 24 hours",
              },
              {
                icon: Phone,
                title: "Phone Support",
                description: "+1 (555) 123-4567",
                action: "Call Now",
                available: "Mon-Fri, 9AM-6PM EST",
              },
            ].map((contact, index) => (
              <div
                key={index}
                className="p-6 sm:p-8 rounded-2xl border border-gray-200 hover:border-[#DB4789] hover:shadow-lg transition-all text-center"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-xl bg-[#75055B]/10 flex items-center justify-center mb-3 sm:mb-4">
                  <contact.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#75055B]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black mb-1 sm:mb-2">{contact.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{contact.description}</p>
                <p className="text-xs sm:text-sm text-[#75055B] mb-4 sm:mb-6">{contact.available}</p>
                <Button className="bg-[#75055B] text-white hover:bg-[#DB4789] hover:shadow-lg hover:shadow-[#DB4789]/30 text-sm sm:text-base w-full sm:w-auto">
                  {contact.action}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 px-2">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="p-5 sm:p-6 rounded-xl bg-white border border-gray-200 hover:border-[#DB4789] transition-colors"
              >
                <h3 className="text-base sm:text-lg font-bold text-black mb-2 sm:mb-3">{faq.question}</h3>
                <p className="text-sm sm:text-base text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help Resources */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 px-2">
              More Resources
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: FileQuestion,
                title: "Help Center",
                description: "Browse articles and guides",
              },
              {
                icon: Clock,
                title: "Service Status",
                description: "Check system status",
              },
              {
                icon: Shield,
                title: "Safety Guidelines",
                description: "Stay safe while booking",
              },
            ].map((resource, index) => (
              <div
                key={index}
                className="p-5 sm:p-6 rounded-xl border border-gray-200 hover:border-[#DB4789] hover:shadow-lg transition-all text-center cursor-pointer"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-lg bg-[#75055B] flex items-center justify-center mb-3 sm:mb-4">
                  <resource.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-black mb-1 sm:mb-2">{resource.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{resource.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  )
}

